import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Icon, { IconSizes } from '../../../components/foundations/Icon';
import Table from '../../../components/organisms/Table';
import css from './TableArtifact.css';

const getSeverityBadge = type => {
    const typeStr = String(type).toLowerCase();
    if (typeStr === 'error') {
        return (
            <span className={css.severityBadge} data-severity="error">
                Error
            </span>
        );
    }
    if (typeStr === 'warning') {
        return (
            <span className={css.severityBadge} data-severity="warning">
                Warning
            </span>
        );
    }
    if (typeStr === 'info') {
        return (
            <span className={css.severityBadge} data-severity="info">
                Info
            </span>
        );
    }
    return type;
};

const getSafeUrl = value => {
    if (typeof value !== 'string') return null;
    try {
        const url = new URL(value);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
            return url.href;
        }
    } catch {
        return null;
    }
    return null;
};

const formatCellValue = (value, field) => {
    if (field === 'type') {
        return getSeverityBadge(value);
    }
    if (field === 'help_center_link' && value) {
        const safeUrl = getSafeUrl(value);
        if (!safeUrl) return '-';
        return (
            <a href={safeUrl} target="_blank" rel="noopener noreferrer" className={css.helpLink}>
                <Icon name="external-link" size={IconSizes.SMALL} />
            </a>
        );
    }
    return value || '-';
};

// Factory function to create a custom cell component for a specific field
const createCellComponent = field => {
    const CustomCell = ({ value }) => <td>{formatCellValue(value, field)}</td>;
    CustomCell.propTypes = {
        value: PropTypes.any,
    };
    return CustomCell;
};

const TableArtifact = ({ artifact }) => {
    const { data, metadata } = artifact;
    // Data is now directly the content object
    const content = data || {};
    const columns = content.columns || [];
    const rows = content.rows || [];
    const summary = content.summary || metadata || {};
    const title = content?.title || '';

    // Transform columns to Table component metadata format
    const tableMetadata = useMemo(() => {
        return columns.map(col => ({
            name: col.field,
            displayName: col.label || col.field,
            headerAlign: 'left',
            width: col.width,
            cellComponent: createCellComponent(col.field),
        }));
    }, [columns]);

    if (!rows.length) {
        return (
            <div className={css.emptyState}>
                <Icon name="table" size={IconSizes.LARGE} />
                <div>No data available</div>
            </div>
        );
    }

    return (
        <div className={css.tableArtifact}>
            <div className={css.tableHeader}>
                {title && <div className={css.tableTitle}>{title}</div>}
                {summary && (
                    <div className={css.tableSummary}>
                        {summary.total && (
                            <span className={css.summaryItem}>
                                Total: <strong>{summary.total}</strong>
                            </span>
                        )}
                        {summary.errors > 0 && (
                            <span className={css.summaryItem} data-type="error">
                                <Icon name="exclamation-circle" size={IconSizes.SMALL} />
                                {summary.errors} Error{summary.errors !== 1 ? 's' : ''}
                            </span>
                        )}
                        {summary.warnings > 0 && (
                            <span className={css.summaryItem} data-type="warning">
                                <Icon name="exclamation-triangle" size={IconSizes.SMALL} />
                                {summary.warnings} Warning{summary.warnings !== 1 ? 's' : ''}
                            </span>
                        )}
                        {summary.info > 0 && (
                            <span className={css.summaryItem} data-type="info">
                                <Icon name="info-circle" size={IconSizes.SMALL} />
                                {summary.info} Info
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className={css.tableWrapper}>
                <Table metadata={tableMetadata} data={rows} zebraTable={false} showInnerBorder />
            </div>
        </div>
    );
};

TableArtifact.propTypes = {
    artifact: PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        data: PropTypes.shape({
            columns: PropTypes.arrayOf(
                PropTypes.shape({
                    field: PropTypes.string.isRequired,
                    label: PropTypes.string,
                    width: PropTypes.string,
                })
            ),
            rows: PropTypes.arrayOf(PropTypes.object),
            summary: PropTypes.shape({
                total: PropTypes.number,
                errors: PropTypes.number,
                warnings: PropTypes.number,
                info: PropTypes.number,
            }),
        }),
        metadata: PropTypes.object,
    }).isRequired,
};

export default TableArtifact;
